import { useEffect, useState } from "react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Download,
    Loader2,
    ArrowUp,
    ArrowDown,
    ListChecks,
    Filter,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { toDdMmYyyy } from "@/lib/dateFormat";

const ANY = "__any__";

// Canonical column order matching the spec.
const COLUMNS = [
    ["name", "Name"],
    ["phone", "Phone Number"],
    ["email", "Email"],
    ["college_name", "College Name"],
    ["course", "Course"],
    ["branch", "Branch"],
    ["role", "Role"],
    ["schedule_date", "Schedule Date"],
    ["schedule_time", "Schedule Time"],
    ["otp", "OTP"],
    ["attendance_status", "Attendance Status"],
    ["otp_verification_status", "OTP Verification Status"],
    ["attendance_verified_date", "Attendance Verified Date"],
    ["attendance_verified_time", "Attendance Verified Time"],
    ["whatsapp_status", "WhatsApp Status"],
    ["send_attempts", "Send Attempts"],
    ["registration_type", "Registration Type"],
    ["created_date", "Created Date"],
    ["updated_date", "Updated Date"],
];

const emptyFilters = () => ({
    attendance_status: ANY,
    schedule_date: "",
    role: "",
    college_name: "",
    course: "",
    branch: "",
    whatsapp_status: ANY,
    otp_verification_status: ANY,
    registration_type: ANY,
    created_from: "",
    created_to: "",
});

export default function AttendanceExport() {
    const [filters, setFilters] = useState(emptyFilters());
    const [selected, setSelected] = useState(() => COLUMNS.map(([k]) => k));
    const [format, setFormat] = useState("csv");
    const [downloading, setDownloading] = useState(false);
    const [facets, setFacets] = useState({ roles: [], colleges: [] });

    useEffect(() => {
        api.get("/candidates/facets")
            .then(({ data }) =>
                setFacets({ roles: data.roles || [], colleges: data.colleges || [] }),
            )
            .catch(() => {
                /* ignore */
            });
    }, []);

    const toggle = (key) => {
        setSelected((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
        );
    };

    const move = (key, dir) => {
        setSelected((prev) => {
            const idx = prev.indexOf(key);
            if (idx === -1) return prev;
            const next = [...prev];
            const swap = idx + (dir === "up" ? -1 : 1);
            if (swap < 0 || swap >= next.length) return prev;
            [next[idx], next[swap]] = [next[swap], next[idx]];
            return next;
        });
    };

    const selectAll = () => setSelected(COLUMNS.map(([k]) => k));
    const clearAll = () => setSelected([]);

    const buildPayload = () => {
        const f = {};
        if (filters.attendance_status !== ANY)
            f.attendance_status = filters.attendance_status;
        if (filters.otp_verification_status !== ANY)
            f.otp_verification_status = filters.otp_verification_status;
        if (filters.registration_type !== ANY)
            f.registration_type = filters.registration_type;
        if (filters.whatsapp_status !== ANY)
            f.whatsapp_status = filters.whatsapp_status;
        if (filters.schedule_date)
            f.schedule_date = toDdMmYyyy(filters.schedule_date);
        if (filters.role.trim()) f.role = filters.role.trim();
        if (filters.college_name.trim()) f.college_name = filters.college_name.trim();
        if (filters.course.trim()) f.course = filters.course.trim();
        if (filters.branch.trim()) f.branch = filters.branch.trim();
        if (filters.created_from)
            f.created_from = filters.created_from + "T00:00:00";
        if (filters.created_to) f.created_to = filters.created_to + "T23:59:59";
        return { filters: f, columns: selected, format };
    };

    const download = async () => {
        if (selected.length === 0) {
            toast.error("Select at least one column.");
            return;
        }
        setDownloading(true);
        try {
            const resp = await api.post("/candidates/export", buildPayload(), {
                responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([resp.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = format === "xlsx" ? "candidates.xlsx" : "candidates.csv";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Export downloaded");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div data-testid="attendance-export-page">
            <AdminPageHeader
                eyebrow="Attendance"
                title="Attendance Export"
                description="Download filtered candidate + attendance data as CSV or Excel. Pick the columns you want and reorder them before downloading."
            />

            <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Filters */}
                <div className="lg:col-span-7 bg-white border border-[#0F1F38]/10 p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Filters
                        </div>
                        <button
                            data-testid="btn-clear-filters"
                            onClick={() => setFilters(emptyFilters())}
                            className="text-xs inline-flex items-center gap-1 text-[#0F1F38]/60 hover:text-[#0F1F38]"
                        >
                            <X size={12} /> Reset
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <FilterSelect
                            label="Attendance Status"
                            testid="f-attendance"
                            value={filters.attendance_status}
                            onValue={(v) =>
                                setFilters({ ...filters, attendance_status: v })
                            }
                            options={[
                                ["Attended", "Attended"],
                                ["Not Attended", "Not Attended"],
                            ]}
                        />
                        <FilterSelect
                            label="OTP Verification"
                            testid="f-otp-verif"
                            value={filters.otp_verification_status}
                            onValue={(v) =>
                                setFilters({ ...filters, otp_verification_status: v })
                            }
                            options={[
                                ["Verified", "Verified"],
                                ["Pending", "Pending"],
                            ]}
                        />
                        <FilterSelect
                            label="WhatsApp Status"
                            testid="f-wa"
                            value={filters.whatsapp_status}
                            onValue={(v) =>
                                setFilters({ ...filters, whatsapp_status: v })
                            }
                            options={[
                                ["sent", "Sent"],
                                ["not_sent", "Not Sent"],
                            ]}
                        />
                        <FilterSelect
                            label="Registration Type"
                            testid="f-regtype"
                            value={filters.registration_type}
                            onValue={(v) =>
                                setFilters({ ...filters, registration_type: v })
                            }
                            options={[
                                ["Excel Upload", "Excel Upload"],
                                ["Self Registration", "Self Registration"],
                            ]}
                        />
                        <FilterInput
                            label="Schedule Date"
                            testid="f-date"
                            type="date"
                            value={filters.schedule_date}
                            onValue={(v) =>
                                setFilters({ ...filters, schedule_date: v })
                            }
                        />
                        <FilterAutocompleteSelect
                            label="Role"
                            testid="f-role"
                            value={filters.role}
                            onValue={(v) => setFilters({ ...filters, role: v })}
                            options={facets.roles}
                        />
                        <FilterAutocompleteSelect
                            label="College Name"
                            testid="f-college"
                            value={filters.college_name}
                            onValue={(v) =>
                                setFilters({ ...filters, college_name: v })
                            }
                            options={facets.colleges}
                        />
                        <FilterInput
                            label="Course"
                            testid="f-course"
                            value={filters.course}
                            onValue={(v) => setFilters({ ...filters, course: v })}
                        />
                        <FilterInput
                            label="Branch"
                            testid="f-branch"
                            value={filters.branch}
                            onValue={(v) => setFilters({ ...filters, branch: v })}
                        />
                        <FilterInput
                            label="Created From"
                            testid="f-created-from"
                            type="date"
                            value={filters.created_from}
                            onValue={(v) =>
                                setFilters({ ...filters, created_from: v })
                            }
                        />
                        <FilterInput
                            label="Created To"
                            testid="f-created-to"
                            type="date"
                            value={filters.created_to}
                            onValue={(v) => setFilters({ ...filters, created_to: v })}
                        />
                    </div>
                </div>

                {/* Columns + format + download */}
                <div className="lg:col-span-5 space-y-6">
                    <div className="bg-white border border-[#0F1F38]/10 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                                Columns ({selected.length})
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    data-testid="btn-select-all"
                                    onClick={selectAll}
                                    className="text-xs text-[#0F1F38]/70 hover:text-[#0F1F38]"
                                >
                                    Select all
                                </button>
                                <span className="text-[#0F1F38]/20">|</span>
                                <button
                                    data-testid="btn-clear-cols"
                                    onClick={clearAll}
                                    className="text-xs text-[#0F1F38]/70 hover:text-[#0F1F38]"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                        <div
                            data-testid="columns-list"
                            className="border border-[#0F1F38]/10 divide-y max-h-96 overflow-auto"
                        >
                            {/* Selected (ordered) */}
                            {selected.map((key) => {
                                const label = COLUMNS.find(([k]) => k === key)?.[1] || key;
                                return (
                                    <div
                                        key={key}
                                        data-testid={`col-${key}`}
                                        className="flex items-center gap-2 px-3 py-2 bg-[#F7F5F0]/50"
                                    >
                                        <Checkbox
                                            checked
                                            onCheckedChange={() => toggle(key)}
                                            data-testid={`col-check-${key}`}
                                        />
                                        <span className="flex-1 text-sm">{label}</span>
                                        <button
                                            data-testid={`col-up-${key}`}
                                            onClick={() => move(key, "up")}
                                            className="p-1 hover:bg-[#0F1F38]/5"
                                        >
                                            <ArrowUp size={12} />
                                        </button>
                                        <button
                                            data-testid={`col-down-${key}`}
                                            onClick={() => move(key, "down")}
                                            className="p-1 hover:bg-[#0F1F38]/5"
                                        >
                                            <ArrowDown size={12} />
                                        </button>
                                    </div>
                                );
                            })}
                            {/* Unselected — greyed */}
                            {COLUMNS.filter(([k]) => !selected.includes(k)).map(
                                ([key, label]) => (
                                    <div
                                        key={key}
                                        className="flex items-center gap-2 px-3 py-2 text-[#0F1F38]/50"
                                    >
                                        <Checkbox
                                            checked={false}
                                            onCheckedChange={() => toggle(key)}
                                            data-testid={`col-check-${key}`}
                                        />
                                        <span className="flex-1 text-sm">{label}</span>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>

                    <div className="bg-white border border-[#0F1F38]/10 p-5">
                        <div className="text-[10px] uppercase tracking-label text-[#0F1F38]/60 mb-3">
                            Format
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-4">
                            {["csv", "xlsx"].map((f) => (
                                <button
                                    key={f}
                                    data-testid={`format-${f}`}
                                    onClick={() => setFormat(f)}
                                    className={`py-2 text-sm font-medium border ${
                                        format === f
                                            ? "bg-[#0F1F38] text-[#F7F5F0] border-[#0F1F38]"
                                            : "border-[#0F1F38]/20 text-[#0F1F38] hover:border-[#0F1F38]"
                                    }`}
                                >
                                    {f.toUpperCase()}
                                </button>
                            ))}
                        </div>
                        <button
                            data-testid="btn-download-export"
                            onClick={download}
                            disabled={downloading}
                            className="w-full bg-emerald-600 text-white py-3 text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-emerald-700 disabled:opacity-50"
                        >
                            {downloading ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Download size={14} />
                            )}
                            Download
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FilterSelect({ label, testid, value, onValue, options }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                {label}
            </Label>
            <Select value={value} onValueChange={onValue}>
                <SelectTrigger data-testid={testid}>
                    <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={ANY}>Any</SelectItem>
                    {options.map(([v, lab]) => (
                        <SelectItem key={v} value={v}>
                            {lab}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

function FilterInput({ label, testid, value, onValue, type = "text" }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                {label}
            </Label>
            <Input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onValue(e.target.value)}
            />
        </div>
    );
}

function FilterAutocompleteSelect({ label, testid, value, onValue, options }) {
    // Freeform input with datalist so admins can either pick a known value or type one.
    const listId = `${testid}-list`;
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                {label}
            </Label>
            <input
                data-testid={testid}
                list={listId}
                value={value}
                onChange={(e) => onValue(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                placeholder="Any"
            />
            <datalist id={listId}>
                {options.map((o) => (
                    <option key={o} value={o} />
                ))}
            </datalist>
        </div>
    );
}
